import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Pin, Play, MapPin, Clock, Flag } from "lucide-react";

const COMPETITION_COLOR = {
  "Championnat": "bg-[#1E3A8A] text-white",
  "Coupe": "bg-[#D97706] text-white",
  "Amical": "bg-slate-500 text-white",
  "Tournoi": "bg-emerald-600 text-white",
};

const STATUS_META = {
  scheduled: { label: "À venir", color: "bg-slate-200 text-slate-700" },
  live: { label: "🔴 EN DIRECT", color: "bg-[#DC2626] text-white animate-pulse" },
  finished: { label: "Terminé", color: "bg-slate-700 text-white" },
};

function youtubeEmbed(url) {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

function tiktokEmbed(url) {
  const m = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  return m ? `https://www.tiktok.com/embed/${m[1]}` : null;
}

function MediaCard({ item }) {
  const yt = item.type === "youtube" ? youtubeEmbed(item.url) : null;
  const tk = item.type === "tiktok" ? tiktokEmbed(item.url) : null;

  if (yt) {
    return (
      <div className="rounded-lg overflow-hidden border border-[#E2E8F0] bg-black" data-testid="media-youtube">
        <div className="aspect-video">
          <iframe src={yt} className="w-full h-full" allowFullScreen title={item.caption || "Vidéo"} />
        </div>
        {(item.caption || item.minute != null) && (
          <div className="p-2.5 text-xs text-white bg-black/80 flex items-center justify-between">
            <span>{item.caption}</span>
            {item.minute != null && <span className="font-bold text-[#D97706]">⚽ {item.minute}'</span>}
          </div>
        )}
      </div>
    );
  }
  if (tk) {
    return (
      <div className="rounded-lg overflow-hidden border border-[#E2E8F0] bg-black" data-testid="media-tiktok">
        <div style={{ aspectRatio: "9/16" }}>
          <iframe src={tk} className="w-full h-full" allowFullScreen title={item.caption || "TikTok"} />
        </div>
        {item.caption && <div className="p-2.5 text-xs text-white bg-black/80">{item.caption}</div>}
      </div>
    );
  }
  if (item.type === "video") {
    return (
      <div className="rounded-lg overflow-hidden border border-[#E2E8F0] bg-black" data-testid="media-video">
        <video controls src={item.url} className="w-full aspect-video object-cover bg-black" />
        {(item.caption || item.minute != null) && (
          <div className="p-2.5 text-xs text-white bg-black/80 flex items-center justify-between">
            <span>{item.caption}</span>
            {item.minute != null && <span className="font-bold text-[#D97706]">⚽ {item.minute}'</span>}
          </div>
        )}
      </div>
    );
  }
  // Photo
  return (
    <div className="rounded-lg overflow-hidden border border-[#E2E8F0] bg-white" data-testid="media-photo">
      <img src={item.url} alt={item.caption || "Photo"} className="w-full aspect-video object-cover" />
      {item.caption && <div className="p-2.5 text-xs text-[#475569]">{item.caption}</div>}
    </div>
  );
}

function MatchCard({ match, large = false }) {
  const compColor = COMPETITION_COLOR[match.competition] || "bg-slate-500 text-white";
  const status = STATUS_META[match.status] || STATUS_META.scheduled;
  const d = new Date(match.date + "T00:00:00");
  const dateStr = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const hasScore = match.score_us != null && match.score_them != null;
  const home = match.is_home ? "VUFC" : match.opponent;
  const away = match.is_home ? match.opponent : "VUFC";
  const homeScore = match.is_home ? match.score_us : match.score_them;
  const awayScore = match.is_home ? match.score_them : match.score_us;

  return (
    <article className={`rounded-xl overflow-hidden border border-[#E2E8F0] bg-white hover:shadow-xl transition-shadow ${large ? "lg:col-span-2" : ""}`} data-testid={`match-${match.id}`}>
      {/* Header */}
      <div className="p-5 bg-gradient-to-br from-[#0F172A] via-[#0F172A] to-[#1E3A8A] text-white">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${compColor} uppercase tracking-widest`}>{match.competition}</span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-white/15 uppercase tracking-widest">{match.category}</span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${status.color} uppercase tracking-widest`}>{status.label}</span>
          {match.pinned && <Pin size={13} className="text-[#D97706] ml-auto" />}
        </div>

        {/* Score board */}
        <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
          <div className="text-right">
            <div className={`font-bold ${large ? "text-2xl" : "text-xl"} truncate`} style={{ fontFamily: "Outfit" }}>{home}</div>
            <div className="text-xs text-slate-300 mt-0.5">{match.is_home ? "Domicile" : "Extérieur"}</div>
          </div>
          <div className={`font-black ${large ? "text-5xl" : "text-4xl"} text-[#D97706]`} style={{ fontFamily: "Outfit" }}>
            {hasScore ? `${homeScore} – ${awayScore}` : "VS"}
          </div>
          <div className="text-left">
            <div className={`font-bold ${large ? "text-2xl" : "text-xl"} truncate`} style={{ fontFamily: "Outfit" }}>{away}</div>
            <div className="text-xs text-slate-300 mt-0.5">{match.is_home ? "" : "Domicile"}</div>
          </div>
        </div>

        {/* Date / lieu / heure */}
        <div className="mt-4 flex items-center flex-wrap gap-4 text-xs text-slate-200">
          <span className="flex items-center gap-1 capitalize"><Flag size={12} /> {dateStr}</span>
          {match.kickoff_time && <span className="flex items-center gap-1"><Clock size={12} /> {match.kickoff_time}</span>}
          <span className="flex items-center gap-1"><MapPin size={12} /> {match.location}</span>
        </div>
      </div>

      {/* Summary */}
      {match.summary && (
        <div className="px-5 py-4 text-sm text-[#475569] border-b border-[#E2E8F0] italic leading-relaxed">
          "{match.summary}"
        </div>
      )}

      {/* Media */}
      {match.media && match.media.length > 0 && (
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Play size={14} className="text-[#D97706]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#0F172A]">
              Vidéos & photos ({match.media.length})
            </span>
          </div>
          <div className={`grid gap-3 ${match.media.some((m) => m.type === "tiktok") ? "sm:grid-cols-2 md:grid-cols-3" : "sm:grid-cols-2"}`}>
            {match.media.map((m, i) => <MediaCard key={i} item={m} />)}
          </div>
        </div>
      )}
    </article>
  );
}

export default function LiveMatches() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/live-matches")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // Group matches
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 3600 * 1000);
  const pinned = items.filter((m) => m.pinned);
  const live = items.filter((m) => !m.pinned && m.status === "live");
  const recent = items.filter((m) => !m.pinned && m.status !== "live" && new Date(m.date) >= sevenDaysAgo);
  const older = items.filter((m) => !m.pinned && m.status !== "live" && new Date(m.date) < sevenDaysAgo);

  return (
    <div data-testid="live-matches-page">
      {/* HERO */}
      <section className="bg-gradient-to-br from-[#DC2626] via-[#B91C1C] to-[#7F1D1D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-14">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Live Matchs
          </div>
          <h1 className="font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight" style={{ fontFamily: "Outfit" }}>
            Les scores, les vidéos, les <span className="text-[#FEF3C7]">émotions</span>.
          </h1>
          <p className="mt-4 text-red-100 max-w-2xl leading-relaxed">
            Suivez chaque week-end nos équipes en direct : scores à chaud, buts marqués, célébrations et moments forts.
            Les coachs envoient les vidéos juste après le match, elles arrivent ici en quelques minutes.
          </p>
        </div>
      </section>

      {/* PARENTS & COACHES CALLOUT */}
      <section className="bg-[#FEF3C7] border-b border-[#F59E0B]/30" data-testid="coach-callout">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[#D97706] text-white flex items-center justify-center shrink-0 mt-0.5 font-bold text-sm">📹</div>
            <div className="text-sm">
              <span className="font-bold text-[#92400E]">Parents & coachs du VUFC</span>
              <span className="text-[#78350F]"> — vous avez filmé un but, un geste technique ou une belle action ? Envoyez-la à l'adresse dédiée pour qu'elle apparaisse sur cette page :</span>
            </div>
          </div>
          <a
            href="mailto:vufc69100@yahoo.com?subject=Vid%C3%A9o%20match%20VUFC"
            data-testid="coach-email-cta"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#D97706] hover:bg-[#B45309] text-white font-bold text-sm transition-colors whitespace-nowrap"
          >
            ✉️ vufc69100@yahoo.com
          </a>
        </div>
      </section>

      {/* CONTENT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-center text-sm text-[#475569]">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="p-10 border border-dashed border-[#E2E8F0] rounded-lg text-center" data-testid="live-empty">
            <Trophy className="h-10 w-10 text-[#D97706] mx-auto mb-3" />
            <div className="font-bold text-[#0F172A] text-lg" style={{ fontFamily: "Outfit" }}>Aucun match publié pour l'instant</div>
            <p className="mt-2 text-sm text-[#475569] max-w-md mx-auto">
              Revenez ce week-end : nos équipes jouent le samedi et le dimanche, et les vidéos sont ajoutées juste après chaque match.
            </p>
          </div>
        ) : (
          <>
            {pinned.length > 0 && (
              <div className="mb-10">
                <SectionTitle label="À la une" />
                <div className="grid gap-6 lg:grid-cols-2">
                  {pinned.map((m) => <MatchCard key={m.id} match={m} large />)}
                </div>
              </div>
            )}
            {live.length > 0 && (
              <div className="mb-10">
                <SectionTitle label="🔴 En direct maintenant" red />
                <div className="grid gap-6 lg:grid-cols-2">
                  {live.map((m) => <MatchCard key={m.id} match={m} />)}
                </div>
              </div>
            )}
            {recent.length > 0 && (
              <div className="mb-10">
                <SectionTitle label="Ce week-end" />
                <div className="grid gap-6 lg:grid-cols-2">
                  {recent.map((m) => <MatchCard key={m.id} match={m} />)}
                </div>
              </div>
            )}
            {older.length > 0 && (
              <div className="mb-10">
                <SectionTitle label="Les semaines précédentes" />
                <div className="grid gap-6 lg:grid-cols-2">
                  {older.map((m) => <MatchCard key={m.id} match={m} />)}
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function SectionTitle({ label, red = false }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <span className={`text-xs tracking-[0.3em] uppercase font-bold ${red ? "text-[#DC2626]" : "text-[#D97706]"}`}>{label}</span>
      <div className="flex-1 h-px bg-[#E2E8F0]" />
    </div>
  );
}
